// src/pages/dashboard/Dashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Segmented, Skeleton } from 'antd';
import {
  UsergroupAddOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  ArrowRightOutlined,
  CalendarOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { supabase } from '../../services/supabase';
import { useTheme } from '../../context/ThemeContext';
import {
  Page,
  PageHeader,
  CardTitle,
  StatGrid,
  StatCard,
  EmptyState,
  StatusPill,
  PersonCell,
} from '../../components/ui';

// A timesheet row counts as "present" when the employee actually clocked in.
// Leave and absence rows (leave_type / status 'absent') are not attendance.
const isPresent = (record) =>
  Boolean(record.check_in) &&
  record.check_in !== '00:00:00' &&
  !record.leave_type &&
  record.status !== 'absent';

const STATUS_TONES = { active: 'positive', inactive: 'neutral', terminated: 'danger' };

const monthStartFor = (which) =>
  which === 'this' ? dayjs().startOf('month') : dayjs().subtract(1, 'month').startOf('month');

// One point per recorded day of the month: how many clocked in, and how many of those were late
const buildDailySeries = (records, monthKey) => {
  const byDate = {};
  records
    .filter((r) => String(r.date).startsWith(monthKey))
    .forEach((r) => {
      if (!byDate[r.date]) byDate[r.date] = { date: r.date, present: 0, late: 0 };
      if (isPresent(r)) {
        byDate[r.date].present += 1;
        if (r.is_late) byDate[r.date].late += 1;
      }
    });
  return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
};

const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const AttendanceTooltip = ({ active, payload, activeCount }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="chart-tooltip">
      <span className="chart-tooltip__title">{dayjs(point.date).format('ddd D MMM')}</span>
      <div>
        Present <strong>{point.present}</strong>
        {activeCount ? ` of ${activeCount}` : ''}
      </div>
      <div>
        Late <strong>{point.late}</strong>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { colors, tokens } = useTheme();
  const [loading, setLoading] = useState(true);
  const [chartMonth, setChartMonth] = useState('this');
  const [data, setData] = useState({
    employees: [],
    todayRecords: [],
    monthRecords: [],
    upcomingHolidays: [],
    recentEmployees: [],
  });

  useEffect(() => {
    fetchCompanyId();
  }, []);

  const fetchCompanyId = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: companyData, error } = await supabase
        .from('companies')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      fetchDashboardData(companyData.id);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const fetchDashboardData = async (company_id) => {
    try {
      setLoading(true);
      const today = dayjs().format('YYYY-MM-DD');
      // Two months of records so the chart can switch between this month and last month
      const rangeStart = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
      const rangeEnd = dayjs().endOf('month').format('YYYY-MM-DD');

      const [employeesRes, todayRes, holidaysRes, monthRes, recentRes] = await Promise.all([
        supabase.from('employees').select('*').eq('company_id', company_id),
        supabase
          .from('timesheet_daily_records')
          .select('employee_id, check_in, is_late, leave_type, status')
          .eq('company_id', company_id)
          .eq('date', today),
        supabase
          .from('holidays')
          .select('*')
          .eq('company_id', company_id)
          .gte('date', today)
          .order('date')
          .limit(5),
        supabase
          .from('timesheet_daily_records')
          .select('date, check_in, is_late, leave_type, status')
          .eq('company_id', company_id)
          .gte('date', rangeStart)
          .lte('date', rangeEnd),
        supabase
          .from('employees')
          .select('*')
          .eq('company_id', company_id)
          .order('created_at', { ascending: false })
          .limit(5),
      ]);

      const monthRecords = monthRes.data || [];
      setData({
        employees: employeesRes.data || [],
        todayRecords: todayRes.data || [],
        upcomingHolidays: holidaysRes.data || [],
        monthRecords,
        recentEmployees: recentRes.data || [],
      });

      // Early in the month there is little to chart, so open on last month instead
      const thisMonthDays = buildDailySeries(monthRecords, monthStartFor('this').format('YYYY-MM')).length;
      const lastMonthDays = buildDailySeries(monthRecords, monthStartFor('last').format('YYYY-MM')).length;
      if (thisMonthDays < 5 && lastMonthDays > thisMonthDays) {
        setChartMonth('last');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ---- Today ----
  const stats = useMemo(() => {
    const total = data.employees.length;
    const active = data.employees.filter((e) => e.status === 'active').length;
    const present = data.todayRecords.filter(isPresent);
    const presentCount = present.length;
    const lateCount = present.filter((r) => r.is_late).length;
    // Absence is only known once today's attendance has been imported
    const imported = data.todayRecords.length > 0;
    const absentCount = imported ? Math.max(0, active - presentCount) : null;
    return { total, active, inactive: total - active, presentCount, lateCount, absentCount, imported };
  }, [data.employees, data.todayRecords]);

  // ---- Chart ----
  const chartMonthStart = monthStartFor(chartMonth);

  const chartData = useMemo(
    () => buildDailySeries(data.monthRecords, monthStartFor(chartMonth).format('YYYY-MM')),
    [data.monthRecords, chartMonth]
  );

  const chartSummary = useMemo(() => {
    if (!chartData.length) return null;
    const avgPresent = chartData.reduce((sum, d) => sum + d.present, 0) / chartData.length;
    const lateTotal = chartData.reduce((sum, d) => sum + d.late, 0);
    const rate = stats.active ? Math.round((avgPresent / stats.active) * 100) : null;
    return { days: chartData.length, rate, lateTotal };
  }, [chartData, stats.active]);

  // Round the axis up to a multiple of 5 so ticks fall on even steps
  const yMax = Math.ceil(Math.max(stats.active, ...chartData.map((d) => d.present), 5) / 5) * 5;
  const yStep = yMax <= 30 ? 5 : yMax <= 60 ? 10 : Math.ceil(yMax / 50) * 10;
  const yTicks = Array.from({ length: Math.floor(yMax / yStep) + 1 }, (_, i) => i * yStep);
  const lateColor = tokens.warningInk;

  // ---- Recent employees ----
  const recentEmployeeColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <PersonCell
          name={`${record.first_name || ''} ${record.last_name || ''}`.trim() || 'Unnamed'}
          sub={record.fingerprint_id ? `ID ${record.fingerprint_id}` : record.employee_number}
        />
      ),
    },
    {
      title: 'Department',
      dataIndex: 'department',
      render: (department) => (department ? <span className="chip">{department}</span> : <span className="text-muted">—</span>),
    },
    {
      title: 'Position',
      dataIndex: 'position',
      render: (position) => position || <span className="text-muted">—</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => (
        <StatusPill tone={STATUS_TONES[status] || 'neutral'}>
          {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown'}
        </StatusPill>
      ),
    },
  ];

  const today = dayjs();

  return (
    <Page>
      <PageHeader
        title={greeting()}
        subtitle={today.format('dddd, D MMMM YYYY')}
        actions={
          <>
            <Button icon={<UploadOutlined />} onClick={() => navigate('/dashboard/attendance?tab=import')}>
              Import attendance
            </Button>
            <Button type="primary" onClick={() => navigate('/dashboard/payroll')}>
              Run payroll <ArrowRightOutlined />
            </Button>
          </>
        }
      />

      {/* Today */}
      <StatGrid columns={4}>
        <StatCard
          label="Total employees"
          tone="primary"
          icon={<UsergroupAddOutlined />}
          value={stats.total}
          meter={stats.total ? stats.active / stats.total : 0}
          caption={`${stats.active} active · ${stats.inactive} inactive`}
          loading={loading}
        />
        <StatCard
          label="Present today"
          tone="positive"
          icon={<CheckCircleOutlined />}
          value={stats.presentCount}
          unit={stats.active ? `/ ${stats.active}` : undefined}
          meter={stats.active ? stats.presentCount / stats.active : 0}
          caption={
            stats.imported
              ? `${Math.round((stats.presentCount / (stats.active || 1)) * 100)}% of active staff`
              : 'No clock-ins imported yet today'
          }
          loading={loading}
        />
        <StatCard
          label="Late today"
          tone="warning"
          icon={<ClockCircleOutlined />}
          value={stats.imported ? stats.lateCount : '—'}
          meter={stats.presentCount ? stats.lateCount / stats.presentCount : 0}
          caption={
            stats.imported
              ? `${stats.lateCount} of ${stats.presentCount} who clocked in`
              : "Shown after today's attendance import"
          }
          loading={loading}
        />
        <StatCard
          label="Absent today"
          tone="danger"
          icon={<CloseCircleOutlined />}
          value={stats.absentCount ?? '—'}
          meter={stats.imported && stats.active ? stats.absentCount / stats.active : 0}
          caption={
            stats.imported
              ? `${Math.round((stats.absentCount / (stats.active || 1)) * 100)}% of active staff`
              : "Shown after today's attendance import"
          }
          loading={loading}
        />
      </StatGrid>

      <div className="dash-grid">
        {/* Attendance chart */}
        <Card
          title={
            <CardTitle
              title="Monthly attendance"
              subtitle={
                chartMonthStart.format('MMMM YYYY') +
                (chartSummary
                  ? ` · ${chartSummary.days} ${chartSummary.days === 1 ? 'day' : 'days'} recorded${chartSummary.rate !== null ? ` · ${chartSummary.rate}% average attendance` : ''} · ${chartSummary.lateTotal} late ${chartSummary.lateTotal === 1 ? 'arrival' : 'arrivals'}`
                  : '')
              }
            />
          }
          extra={
            <Segmented
              size="small"
              value={chartMonth}
              onChange={setChartMonth}
              options={[
                { label: 'This month', value: 'this' },
                { label: 'Last month', value: 'last' },
              ]}
            />
          }
        >
          {loading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : chartData.length === 0 ? (
            <EmptyState
              icon={<ClockCircleOutlined />}
              title={`No attendance recorded for ${chartMonthStart.format('MMMM')}`}
              description="Import the fingerprint file on the Attendance page and daily presence will chart here."
              action={
                <Button icon={<UploadOutlined />} onClick={() => navigate('/dashboard/attendance?tab=import')}>
                  Import attendance
                </Button>
              }
            />
          ) : (
            <>
              <div className="chart-legend" style={{ marginBottom: 12 }}>
                <span className="chart-legend__item">
                  <i className="chart-legend__swatch" style={{ '--swatch': colors.primary }} />
                  Present
                </span>
                <span className="chart-legend__item">
                  <i className="chart-legend__swatch" style={{ '--swatch': lateColor }} />
                  Late
                </span>
                {stats.active > 0 && (
                  <span className="chart-legend__item">
                    <i className="chart-legend__swatch chart-legend__swatch--line" style={{ '--swatch': tokens.muted }} />
                    Active staff
                  </span>
                )}
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={chartData} margin={{ top: 8, right: 64, bottom: 0, left: -18 }}>
                  <defs>
                    <linearGradient id="presentFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={colors.primary} stopOpacity={0.26} />
                      <stop offset="100%" stopColor={colors.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} stroke={tokens.line} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => dayjs(d).format('D MMM')}
                    tick={{ fill: tokens.muted, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={18}
                  />
                  <YAxis
                    allowDecimals={false}
                    domain={[0, yMax]}
                    ticks={yTicks}
                    tick={{ fill: tokens.muted, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={<AttendanceTooltip activeCount={stats.active} />}
                    cursor={{ stroke: tokens.lineStrong }}
                  />
                  {stats.active > 0 && (
                    <ReferenceLine
                      y={stats.active}
                      stroke={tokens.muted}
                      strokeDasharray="4 4"
                      label={{
                        value: `${stats.active} active`,
                        position: 'right',
                        fill: tokens.muted,
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    />
                  )}
                  <Bar animationDuration={700} dataKey="late" name="Late" fill={lateColor} barSize={10} radius={[2, 2, 0, 0]} />
                  <Area
                    type="monotone"
                    animationDuration={700}
                    dataKey="present"
                    name="Present"
                    stroke={colors.primary}
                    strokeWidth={2.25}
                    fill="url(#presentFill)"
                    activeDot={{ r: 4.5, fill: tokens.surface, stroke: colors.primary, strokeWidth: 2.5 }}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </>
          )}
        </Card>

        {/* Upcoming holidays */}
        <Card
          title={<CardTitle title="Upcoming holidays" subtitle="Public holidays ahead" />}
          extra={
            <Button type="link" size="small" onClick={() => navigate('/dashboard/settings?tab=holidays')}>
              Manage
            </Button>
          }
          styles={{ body: { paddingTop: 6 } }}
        >
          {loading ? (
            <Skeleton active paragraph={{ rows: 5 }} />
          ) : data.upcomingHolidays.length === 0 ? (
            <EmptyState
              icon={<CalendarOutlined />}
              title="No upcoming holidays"
              description="Add this year's public holidays so they show here and on the attendance sheet."
              action={
                <Button icon={<PlusOutlined />} onClick={() => navigate('/dashboard/settings?tab=holidays')}>
                  Add holidays
                </Button>
              }
            />
          ) : (
            <ul className="holiday-list">
              {data.upcomingHolidays.map((holiday) => {
                const date = dayjs(holiday.date);
                const days = date.startOf('day').diff(today.startOf('day'), 'day');
                return (
                  <li className="holiday-list__item" key={holiday.id}>
                    <span className="date-tile" aria-hidden="true">
                      <span className="date-tile__month">{date.format('MMM')}</span>
                      <span className="date-tile__day">{date.format('D')}</span>
                    </span>
                    <span>
                      <span className="holiday-list__name">{holiday.name}</span>
                      <span className="holiday-list__meta">{date.format('dddd, D MMM YYYY')}</span>
                    </span>
                    <span className="countdown-pill">
                      {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `in ${days} days`}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      {/* Recent employees */}
      <Card
        className="table-card"
        title={<CardTitle title="Recent employees" subtitle="Last 5 added" />}
        extra={
          <Button type="link" size="small" onClick={() => navigate('/dashboard/employees')}>
            View all employees
          </Button>
        }
      >
        <Table
          columns={recentEmployeeColumns}
          dataSource={data.recentEmployees}
          rowKey="id"
          pagination={false}
          loading={loading}
          scroll={{ x: 560 }}
        />
      </Card>
    </Page>
  );
};

export default Dashboard;
